// src/hooks/useDepartments.js
import { useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';
import toast from 'react-hot-toast';

/**
 * useDepartments — manages all department state and API interactions.
 * Used by Team.jsx so the page stays clean.
 */
export function useDepartments() {
  const [departments, setDepartments] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  // ── Fetch all departments ────────────────────────────────────
  const fetchDepartments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getDepartments();
      // res.data is the array of departments (each includes .members)
      setDepartments(res.data ?? []);
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to load departments.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDepartments(); }, [fetchDepartments]);

  // ── Create department ────────────────────────────────────────
  const createDepartment = useCallback(async (name, description = '') => {
    try {
      const res = await apiService.createDepartment({ name, description });
      setDepartments(prev => [...prev, res.data]);
      toast.success(`Department "${name}" created.`);
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to create department.';
      toast.error(msg);
      throw err;
    }
  }, []);

  // ── Rename department ────────────────────────────────────────
  const renameDepartment = useCallback(async (departmentId, name) => {
    try {
      const res = await apiService.updateDepartment(departmentId, { name });
      setDepartments(prev =>
        prev.map(d => d.id === departmentId ? res.data : d)
      );
      toast.success('Department renamed.');
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to rename department.';
      toast.error(msg);
      throw err;
    }
  }, []);

  // ── Delete department ────────────────────────────────────────
  const deleteDepartment = useCallback(async (departmentId) => {
    try {
      await apiService.deleteDepartment(departmentId);
      setDepartments(prev => prev.filter(d => d.id !== departmentId));
      toast.success('Department deleted.');
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to delete department.';
      toast.error(msg);
      throw err;
    }
  }, []);

  // ── Add member ───────────────────────────────────────────────
  const addMember = useCallback(async (departmentId, userId, role = 'member') => {
    try {
      const res = await apiService.addDepartmentMember(departmentId, userId, role);
      // res.data is the full updated department with members
      setDepartments(prev =>
        prev.map(d => d.id === departmentId ? res.data : d)
      );
      toast.success('Member added.');
      return res.data;
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to add member.';
      toast.error(msg);
      throw err;
    }
  }, []);

  // ── Remove member ────────────────────────────────────────────
  const removeMember = useCallback(async (departmentId, userId) => {
    try {
      const res = await apiService.removeDepartmentMember(departmentId, userId);
      setDepartments(prev =>
        prev.map(d => d.id === departmentId ? res.data : d)
      );
      toast.success('Member removed.');
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to remove member.';
      toast.error(msg);
      throw err;
    }
  }, []);

  // ── Update member role ───────────────────────────────────────
  const updateMemberRole = useCallback(async (departmentId, userId, role) => {
    try {
      const res = await apiService.updateDepartmentMemberRole(departmentId, userId, role);
      setDepartments(prev =>
        prev.map(d => d.id === departmentId ? res.data : d)
      );
      toast.success('Role updated.');
    } catch (err) {
      const msg = err.response?.data?.message ?? 'Failed to update role.';
      toast.error(msg);
      throw err;
    }
  }, []);

  return {
    departments,
    loading,
    error,
    fetchDepartments,
    createDepartment,
    renameDepartment,
    deleteDepartment,
    addMember,
    removeMember,
    updateMemberRole,
  };
}